import {
  useQueries,
  useQuery,
} from '@tanstack/react-query'
import camelcaseKeys from 'camelcase-keys'
import backend from '@api/backend'
import { DeviceIDTypes } from '@data/device/model/device'
import {
  EventTypes,
  MailEvents,
  MailRecipients,
} from '@data/mail/model/mail'
import { AxiosError } from 'axios'


export const useRecipientByDeviceId = (deviceId: string, templateId: string | null) => {
  return useQuery<MailRecipients>({
    queryKey: ['recipientsDeviceId', deviceId],
    queryFn: ({ signal }) => getByDeviceId(deviceId, signal),
    enabled: !!deviceId && !!templateId,
    staleTime: 1000 * 60 * 2,
  })
}

export interface MailEventQueryProps {
  gid: string
  event?: EventTypes
  mailType?: string
}

export const useMailEvents = (
  props: MailEventQueryProps,
) => {
  return useQuery<MailEvents[], AxiosError>({
    queryKey: ['mailEvents', props.gid],
    queryFn: ({ signal }) => getMailEvents(props, signal),
    staleTime: 1000 * 60 * 2,
  })
}

export const useMailEventsBatch = (
  items: MailEventQueryProps[],
) => {
  return useQueries({
    queries: items.map((item) => ({
      queryKey: ['itemGid', item.gid],
      queryFn: ({ signal }) => getMailEvents(item, signal),
      staleTime: 1000 * 10 * 2,
    })),
  })
}

const getByDeviceId = async (deviceId: string, signal: AbortSignal): Promise<MailRecipients> => {
  const response = await backend.get(`/usage_alerts/${deviceId}/alert_recipients`, {
    signal,
    params: { idType: DeviceIDTypes.dUuid },
  })
  return camelcaseKeys(response.data, { deep: true }) as MailRecipients
}

const getMailEvents = async (params: any, signal: AbortSignal): Promise<MailEvents[]> => {
  const { gid, ...queryParams } = params
  const response = await backend.get(`/mail/events/${gid}`, {
    signal,
    params: { queryParams },
  })
  return response.data
}